<template>
    <UApp :locale="uiLocale">
        <UTheme :props="uiDefaults">
            <!--
                Nothing is drawn until the house is known. Every screen underneath reads
                the property — its name, its colours, its terms, its currency — and drawing
                them against an empty one for a moment is a page that visibly rearranges
                itself while somebody is reading it.
            -->
            <div v-if="loading" class="grid min-h-screen place-items-center bg-muted">
                <div class="flex flex-col items-center gap-3 text-muted">
                    <UIcon name="i-lucide-loader-circle" class="size-7 animate-spin" />
                    <p class="text-sm">{{ $t('general.loading') }}</p>
                </div>
            </div>

            <!-- The hotel has not bought the booking engine. It replaces the site rather
                 than sending the visitor to the "closed" screen: that one is a guess made
                 from an empty answer, and this is the API naming the reason. -->
            <ModuleNotice v-else-if="moduleUnlicensed" />

            <Default v-else />
        </UTheme>
    </UApp>
</template>

<script lang="ts" setup>
    import * as locales from '@nuxt/ui/locale'
    import { computed, onMounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import ModuleNotice from '@/components/ModuleNotice.vue'
    import Default from '@/layouts/Default.vue'
    import { moduleUnlicensed } from '@/libs/license'
    import { uiDefaults } from '@/libs/uiDefaults'
    import { useBookingStore } from '@/stores/booking'

    const store = useBookingStore()
    const router = useRouter()
    const { locale } = useI18n()

    const loading = ref(true)

    // Nuxt UI's own component texts follow the language the guest chose, falling back
    // to English for a locale it does not ship.
    const uiLocale = computed(() => (locales as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    onMounted(async () => {
        await store.loadProperty()

        loading.value = false

        // `closed` is set by the same failed read, so it has to be asked second: a
        // house that has not bought the module is not a house that is not selling
        // today, and the "closed" screen would hide which of the two it is.
        if (store.closed && !moduleUnlicensed.value) await router.replace({ name: 'closed' })
    })
</script>
